/**
 * Fixed UUIDs for default sections matching backend constants.
 * These IDs are stable and don't change even if section names change in the backend.
 */

export const DEFAULT_SECTION_1 = "00000000-0000-0000-0000-000000000001";
export const DEFAULT_SECTION_2 = "00000000-0000-0000-0000-000000000002";
export const DEFAULT_SECTION_3 = "00000000-0000-0000-0000-000000000003";
export const DEFAULT_SECTION_4 = "00000000-0000-0000-0000-000000000004";
export const DEFAULT_SECTION_5 = "00000000-0000-0000-0000-000000000005";
export const DEFAULT_SECTION_6 = "00000000-0000-0000-0000-000000000006";
export const DEFAULT_SECTION_7 = "00000000-0000-0000-0000-000000000007";

/**
 * Check if a section ID matches Default Section 1.
 */
export function isDefaultSection1(sectionId: string | null): boolean {
  return sectionId === DEFAULT_SECTION_1;
}

/**
 * Check if a section ID matches Default Section 2.
 */
export function isDefaultSection2(sectionId: string | null): boolean {
  return sectionId === DEFAULT_SECTION_2;
}

/**
 * Check if a section ID matches Default Section 3.
 */
export function isDefaultSection3(sectionId: string | null): boolean {
  return sectionId === DEFAULT_SECTION_3;
}

/**
 * Check if a section ID matches Default Section 4.
 */
export function isDefaultSection4(sectionId: string | null): boolean {
  return sectionId === DEFAULT_SECTION_4;
}

/**
 * Check if a section ID matches Default Section 5.
 */
export function isDefaultSection5(sectionId: string | null): boolean {
  return sectionId === DEFAULT_SECTION_5;
}

/**
 * Check if a section ID matches Default Section 6.
 */
export function isDefaultSection6(sectionId: string | null): boolean {
  return sectionId === DEFAULT_SECTION_6;
}

/**
 * Check if a section ID matches Default Section 7.
 */
export function isDefaultSection7(sectionId: string | null): boolean {
  return sectionId === DEFAULT_SECTION_7;
}

/**
 * Get the section slot number from a section ID.
 */
export function getSectionSlot(sectionId: string | null): number | null {
  if (sectionId === DEFAULT_SECTION_1) return 1;
  if (sectionId === DEFAULT_SECTION_2) return 2;
  if (sectionId === DEFAULT_SECTION_3) return 3;
  if (sectionId === DEFAULT_SECTION_4) return 4;
  if (sectionId === DEFAULT_SECTION_5) return 5;
  if (sectionId === DEFAULT_SECTION_6) return 6;
  if (sectionId === DEFAULT_SECTION_7) return 7;
  return null;
}

/**
 * Default description for sections.
 */
const DEFAULT_SECTION_DESCRIPTION =
  "Welcome to your dedicated platform for fundraising and investment in Kenya. Fundy guides you through every step—from strategizing your fundraising round to connecting you with top investors. Start a fresh conversation or continue from where you left off.";

/**
 * Registry mapping section IDs to their descriptions.
 * Uses fixed section IDs so descriptions remain stable even if section names change.
 */
const GENERAL_DASHBOARD_DESCRIPTION =
  "Your central hub for fundraising and investment activity. Access recent chats, manage company knowledge, and stay on top of your deal pipeline at a glance.";

const MY_DEAL_NETWORK_DESCRIPTION =
  "Build and nurture relationships with top investors. Easily track communications, manage contacts, and leverage AI insights to strengthen your investor network on your fundraising journey.";

const INVESTOR_READINESS_DESCRIPTION =
  "See where you stand across key investment categories, understand your gaps, and work through to-do items to strengthen your position for investor conversations.";

const SECTION_DESCRIPTIONS: Record<string, string> = {
  [DEFAULT_SECTION_1]: GENERAL_DASHBOARD_DESCRIPTION,
  [DEFAULT_SECTION_2]: INVESTOR_READINESS_DESCRIPTION,
  [DEFAULT_SECTION_3]: DEFAULT_SECTION_DESCRIPTION,
  [DEFAULT_SECTION_4]: MY_DEAL_NETWORK_DESCRIPTION,
  [DEFAULT_SECTION_5]:
    "Manage and track your deal pipeline across sourcing, diligence, and execution. Organize opportunities, monitor deal stages, and collaborate with your team to drive informed investment decisions.",
  [DEFAULT_SECTION_6]:
    "Browse and evaluate deals in your pipeline. Filter by category, review scores and summaries, request introductions, shortlist opportunities, and ask questions directly from each deal card.",
  [DEFAULT_SECTION_7]:
    "Conduct thorough due diligence on investment opportunities. Organize your research, track evaluation progress, and make informed investment decisions with comprehensive diligence tools.",
};

/**
 * Get the description for a section based on its ID.
 * Falls back to default description if section ID is unknown.
 *
 * @param sectionId - The section ID to get the description for
 * @returns The description for the section
 */
export function getSectionDescription(sectionId: string | null): string {
  if (!sectionId) {
    return DEFAULT_SECTION_DESCRIPTION;
  }

  return SECTION_DESCRIPTIONS[sectionId] ?? DEFAULT_SECTION_DESCRIPTION;
}
